import type { VendorMasterRecord } from "./vendorMasterTypes";

export type VendorMasterEditorState =
  | {
      mode: "create";
      vendorId: "";
      expectedUpdatedAt: "";
      vendor: VendorMasterRecord;
    }
  | {
      mode: "edit";
      vendorId: string;
      expectedUpdatedAt: string;
      vendor: VendorMasterRecord;
    };

const pickIdentity = (...values: unknown[]) =>
  values.map((value) => String(value ?? "").trim()).find(Boolean) || "";

export const getCanonicalVendorMasterId = (vendor: VendorMasterRecord = {}) =>
  pickIdentity(vendor.vendorId, vendor.id, vendor.docId, vendor.documentId);

const withoutPersistedIdentity = (vendor: VendorMasterRecord = {}) => {
  const next = { ...vendor };
  delete next.id;
  delete next.vendorId;
  delete next.docId;
  delete next.documentId;
  return next;
};

export const createVendorMasterEditorState = (
  vendor: VendorMasterRecord = {}
): VendorMasterEditorState => ({
  mode: "create",
  vendorId: "",
  expectedUpdatedAt: "",
  vendor: withoutPersistedIdentity(vendor),
});

export const editVendorMasterEditorState = (
  vendor: VendorMasterRecord = {}
): VendorMasterEditorState => {
  const vendorId = getCanonicalVendorMasterId(vendor);
  if (!vendorId) {
    throw new Error("EDITING_VENDOR_ID_MISSING");
  }
  return {
    mode: "edit",
    vendorId,
    expectedUpdatedAt: String(vendor.updatedAt || "").trim(),
    vendor: {
      ...vendor,
      id: vendorId,
      vendorId,
    },
  };
};

export const updateVendorMasterEditorState = (
  state: VendorMasterEditorState,
  patch: Partial<VendorMasterRecord>
): VendorMasterEditorState => {
  const vendor = {
    ...state.vendor,
    ...patch,
    paymentProfile: {
      ...(state.vendor.paymentProfile || {}),
      ...(patch.paymentProfile || {}),
    },
  };
  if (state.mode === "create") {
    return {
      ...state,
      vendor: withoutPersistedIdentity(vendor),
    };
  }
  return {
    ...state,
    vendor: {
      ...vendor,
      id: state.vendorId,
      vendorId: state.vendorId,
    },
  };
};

export const getVendorMasterEditorRequest = (state: VendorMasterEditorState) =>
  state.mode === "edit"
    ? {
        mode: "updateVendor" as const,
        vendorId: state.vendorId,
        expectedUpdatedAt: state.expectedUpdatedAt,
      }
    : {
        mode: "createVendor" as const,
        vendorId: "",
        expectedUpdatedAt: "",
      };
